import React, { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "components/ui/button";
import { cityLabelForMarker, filterMarkersForFleetTable } from "./fleetLocationSearch";

const statusStyles = {
  Healthy: "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Grid Upgrade Needed": "border-amber-200 bg-amber-50 text-amber-700",
  "Charger Downtime": "border-red-200 bg-red-50 text-red-700",
};

const priorityStyles = {
  High: "border-red-200 bg-red-50 text-red-700",
  Medium: "border-amber-200 bg-amber-50 text-amber-700",
  Low: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

function truncateLocationLabel(text, maxLen = 20) {
  const s = text ?? "";
  return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
}

/** Initial batch and each "See more" step: this many site rows per page. */
const EXPANDED_SITES_PAGE_SIZE = 10;

/**
 * Key must match MasterAccessDashboard: username (lower) + trimmed Simplified Domicile
 * (same string as marker `site` / row.location; missing → "Unknown").
 */
function evCountLookupKey(username, location) {
  const domicile = String(location ?? "").trim() || "Unknown";
  return `${String(username || "").toLowerCase()}|||${domicile}`;
}

/** `city info.created_at` from master dashboard (ISO string). */
function formatLastAssessmentFromCreatedAt(iso) {
  if (iso == null || iso === "") return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  return new Date(t).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const FleetLocation = ({
  markers = [],
  isLoadingMarkers = false,
  markersError = null,
  usernames = [],
  programName,
  lastAssessmentCreatedAtByUsername = {},
  evCountByUsernameDomicile = {},
  cityNameByUsername = {},
  filterText = "",
  onFilterTextChange,
}) => {
  /** Per-username expanded state when an account has multiple sites. */
  const [expandedByUser, setExpandedByUser] = useState({});
  /** Per-username: how many site rows to show when expanded; grows by EXPANDED_SITES_PAGE_SIZE per "See more". */
  const [sitesVisibleCountByUser, setSitesVisibleCountByUser] = useState({});

  const fleetRows = useMemo(() => {
    const filtered = filterMarkersForFleetTable(markers, usernames, filterText);
    return filtered.map((m) => ({
      id: m.id,
      username: m.username,
      location: m.site,
      city: cityLabelForMarker(m),
    }));
  }, [markers, usernames, filterText]);

  const rowsByUsername = useMemo(() => {
    const map = new Map();
    for (const row of fleetRows) {
      const key = row.username ?? "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    }

    const accountLabelFor = (username) =>
      cityNameByUsername[String(username || "").toLowerCase()] || username || "—";

    return Array.from(map.entries()).sort(([a], [b]) =>
      accountLabelFor(a).localeCompare(accountLabelFor(b), undefined, {
        sensitivity: "base",
      }),
    );
  }, [fleetRows, cityNameByUsername]);

  const toggleExpanded = (username) => {
    const wasExpanded = !!expandedByUser[username];
    if (wasExpanded) {
      setSitesVisibleCountByUser((prev) => {
        const next = { ...prev };
        delete next[username];
        return next;
      });
    }
    setExpandedByUser((prev) => ({
      ...prev,
      [username]: !prev[username],
    }));
  };

  const metricsCells = (row) => {
    const evCount = evCountByUsernameDomicile[evCountLookupKey(row.username, row.location)];

    return (
      <>
        <td className="px-6 py-4">{row.city}</td>
        <td className="px-6 py-4">{evCount != null ? evCount.toLocaleString("en-US") : "—"}</td>
        <td className="px-6 py-4">
          <span
            className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold ${
              statusStyles["Healthy"] ?? "border-gray-200 bg-gray-50 text-gray-600"
            }`}>
            {row.status ?? "Completed"}
          </span>
        </td>
        <td className="px-6 py-4">
          {formatLastAssessmentFromCreatedAt(
            lastAssessmentCreatedAtByUsername[String(row.username || "").toLowerCase()],
          )}
        </td>
      </>
    );
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
      <header className="border-b border-gray-100 px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Fleet Locations</h3>
          <div className="w-full sm:w-72">
            <input
              type="search"
              value={filterText}
              onChange={(e) => onFilterTextChange?.(e.target.value)}
              placeholder="Filter by username, location, or address"
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              aria-label="Filter fleet locations"
            />
          </div>
        </div>
      </header>
      <div className="overflow-x-auto">
        <table
          className="min-w-full text-sm text-gray-700"
          role="table"
          aria-label="Fleet locations">
          <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th
                className="px-6 py-3 text-left"
                scope="col">
                Location
              </th>
              <th
                className="px-6 py-3 text-left"
                scope="col">
                City
              </th>
              <th
                className="px-6 py-3 text-left normal-case"
                scope="col">
                # EVs
              </th>
              <th
                className="px-6 py-3 text-left"
                scope="col">
                {programName?.trim() + " Status" || "Status"}
              </th>
              <th
                className="px-6 py-3 text-left"
                scope="col">
                Last Assessment
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {isLoadingMarkers && (
              <tr>
                <td
                  className="px-6 py-6 text-center text-sm text-gray-500"
                  colSpan={5}>
                  Loading fleet locations…
                </td>
              </tr>
            )}
            {!isLoadingMarkers && markersError && (
              <tr>
                <td
                  className="px-6 py-6 text-center text-sm text-red-600"
                  colSpan={5}>
                  {markersError}
                </td>
              </tr>
            )}
            {!isLoadingMarkers &&
              !markersError &&
              rowsByUsername.map(([username, rows]) => {
                const safeId = String(username).replace(/[^a-zA-Z0-9_-]/g, "-");
                const sitesGroupId = `fleet-sites-${safeId || "user"}`;
                const accountLabel =
                  cityNameByUsername[String(username || "").toLowerCase()] || username || "—";
                const expanded = !!expandedByUser[username];
                const hasManySites = rows.length > EXPANDED_SITES_PAGE_SIZE;
                const visibleCount = Math.min(
                  sitesVisibleCountByUser[username] ?? EXPANDED_SITES_PAGE_SIZE,
                  rows.length,
                );
                const visibleSiteRows =
                  expanded && hasManySites ? rows.slice(0, visibleCount) : rows;
                const remainingSites = expanded && hasManySites ? rows.length - visibleCount : 0;

                return (
                  <Fragment key={username || rows[0].id}>
                    <tr
                      className="cursor-pointer hover:bg-gray-50/50 focus-visible:bg-gray-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 focus-visible:ring-inset"
                      onClick={() => toggleExpanded(username)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleExpanded(username);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-expanded={expanded}
                      aria-controls={sitesGroupId}
                      title={expanded ? "Collapse sites" : "Expand sites"}>
                      <td className="cursor-pointer pl-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex min-w-0 flex-col gap-0.5 items-center">
                            <div
                              className="text-xs font-semibold text-gray-500 text-left"
                              title={accountLabel}>
                              {accountLabel} ({rows.length} locations)
                            </div>
                          </div>

                          <ChevronRight
                            className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                              expanded ? "rotate-90" : ""
                            }`}
                            aria-hidden
                          />
                        </div>
                      </td>
                      <td
                        colSpan={4}
                        className="cursor-pointer px-6 py-4 text-sm text-gray-400">
                        {expanded ? null : "Expand this row to see every site and its details."}
                      </td>
                    </tr>
                    {expanded &&
                      visibleSiteRows.map((row, idx) => (
                        <tr
                          key={row.id}
                          id={idx === 0 ? sitesGroupId : undefined}
                          className="bg-gray-50/70 hover:bg-gray-50">
                          <td className="border-l-2 border-blue-100 px-6 py-3 pl-8">
                            <span
                              className="font-semibold text-gray-900"
                              title={row.location ?? ""}>
                              {truncateLocationLabel(row.location)}
                            </span>
                          </td>
                          {metricsCells(row)}
                        </tr>
                      ))}
                    {expanded && remainingSites > 0 && (
                      <tr className="bg-gray-50/70">
                        <td
                          colSpan={5}
                          className="border-l-2 border-blue-100 px-6 py-4">
                          <div className="flex flex-col items-center justify-center gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              onClick={() =>
                                setSitesVisibleCountByUser((prev) => {
                                  const current = prev[username] ?? EXPANDED_SITES_PAGE_SIZE;
                                  return {
                                    ...prev,
                                    [username]: Math.min(
                                      current + EXPANDED_SITES_PAGE_SIZE,
                                      rows.length,
                                    ),
                                  };
                                })
                              }
                              aria-label={`See ${Math.min(remainingSites, EXPANDED_SITES_PAGE_SIZE)} more locations`}>
                              See more
                              <ChevronDown
                                className="h-4 w-4 shrink-0 opacity-70"
                                aria-hidden
                              />
                            </Button>
                            <span className="text-xs text-muted-foreground">
                              {remainingSites} {remainingSites === 1 ? "location" : "locations"}{" "}
                              left
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            {!isLoadingMarkers && !markersError && rowsByUsername.length === 0 && (
              <tr>
                <td
                  className="px-6 py-6 text-center text-sm text-gray-500"
                  colSpan={5}>
                  No locations match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FleetLocation;
